import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Modal as UIModal } from "@/components/ui/Modal";
import { formatRelativeTime, formatAbsoluteDate } from "./Shared";
import type { SupportedLocale } from "@chinese-vendor/i18n";

export interface RejectionDetailsModalProps {
  visible: boolean;
  onDismiss: () => void;
  rejectedByName?: string;
  rejectedByRole?: string;
  rejectedAt?: string;
  reason?: string;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  locale: SupportedLocale;
}

export function RejectionDetailsModal({
  visible,
  onDismiss,
  rejectedByName,
  rejectedByRole,
  rejectedAt,
  reason,
  t,
  locale,
}: RejectionDetailsModalProps) {
  return (
    <UIModal
      visible={visible}
      onDismiss={onDismiss}
      title="review.application.rejectionDetails.viewReason"
      closeOnBackdrop
      useScrollView
    >
      {/* REJECTED BY */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.application.rejectionDetails.rejectedBy")}
        </AppText>
        <View className="gap-2">
          <AppText weight="medium" className="text-dark-text-50">
            {rejectedByName ?? "—"}
          </AppText>
          {rejectedByRole && <Badge variant="accent-blue" label={rejectedByRole} />}
        </View>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* DATE REJECTED */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.application.rejectionDetails.dateRejected")}
        </AppText>
        {rejectedAt ? (
          <View className="gap-1">
            <AppText weight="bold" className="text-dark-text-50">
              {formatRelativeTime(rejectedAt, locale, t)}
            </AppText>
            <AppText className="text-sm text-supporting-text">
              {formatAbsoluteDate(rejectedAt, locale)}
            </AppText>
          </View>
        ) : (
          <AppText weight="medium" className="text-dark-text-50">
            —
          </AppText>
        )}
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* REASON */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.application.rejectionDetails.reason")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {reason ?? "—"}
        </AppText>
      </View>
    </UIModal>
  );
}
